import { jsx as _jsx } from "react/jsx-runtime";
import React, { createContext, useContext, useState, useCallback } from 'react';
import i18n from '@application/utilities/i18n';
import { useTranslation } from 'react-i18next';
import { MagicStar, Task, User, Lock, Profile2User, Coin, UserOctagon, Setting2, Grid2, Book1 } from 'iconsax-react';
import { useSelector } from 'react-redux';
const SidebarContext = createContext(undefined);
export const SidebarProvider = ({ children, initialNavigationSections = [], onSearch }) => {
    const [isOpen, setIsOpen] = useState(true);
    const [activeItem, setActiveItem] = useState('home');
    const [navigationSections, setNavigationSections] = useState(initialNavigationSections);
    const [searchValue, setSearchValue] = useState('');
    const { t } = useTranslation();
    const toggleSidebar = useCallback(() => {
        setIsOpen((prev) => !prev);
    }, []);
    const handleSetActiveItem = useCallback((itemId) => {
        setActiveItem(itemId);
    }, []);
    const handleSetNavigationSections = useCallback((sections) => {
        setNavigationSections(sections);
    }, []);
    const currentUser = useSelector((state) => state.auth.currentUser);
    const isCollaborator = Array.isArray(currentUser?.role) &&
        (currentUser?.role.includes('Collaborator') || currentUser?.role.includes('TransportManager'));
    React.useEffect(() => {
        const sections = [
            {
                // title: (t('common.app') as string).toUpperCase(),
                items: [
                    {
                        id: 'home',
                        label: t('common.dashboard'),
                        href: '/',
                        icon: _jsx(Grid2, { variant: 'Outline' })
                    },
                    {
                        id: 'events',
                        label: t('common.events'),
                        //href: '/events-menu',
                        hasSubmenu: true,
                        icon: _jsx(MagicStar, { variant: 'Outline' }),
                        submenuItems: isCollaborator
                            ? [
                                {
                                    id: 'all-events',
                                    label: t('events.allEvents'),
                                    href: '/events',
                                    permission: 'Permissions.VdkEvents.Read'
                                }
                            ]
                            : [
                                {
                                    id: 'create-event',
                                    label: t('events.newEvent'),
                                    href: '/create-event',
                                    permission: 'Permissions.VdkEvents.Create'
                                },
                                {
                                    id: 'all-events',
                                    label: t('events.allEvents'),
                                    href: '/events',
                                    permission: 'Permissions.VdkEvents.Read'
                                }
                                // {
                                //   id: 'event-tasks',
                                //   label: 'Event Tasks',
                                //   href: '/event-tasks',
                                //   icon: <TickSquare set='Outline' primaryColor={theme.palette.grey[600]} />
                                // },
                                // {
                                //   id: 'home-example',
                                //   label: 'Home Example',
                                //   href: '/home-example',
                                //   icon: <Home set='Outline' primaryColor={theme.palette.grey[600]} />
                                // }
                            ]
                    },
                    {
                        id: 'tasks',
                        label: t('common.tasks'),
                        href: '/tasks',
                        icon: _jsx(Task, { variant: 'Outline' }),
                        permission: 'Permissions.VdkTasks.Read'
                    },
                    {
                        id: 'people',
                        label: t('common.people'),
                        hasSubmenu: true,
                        icon: _jsx(Profile2User, { variant: 'Outline' }),
                        submenuItems: [
                            {
                                id: 'vdk-people',
                                label: t('relations.vdkPeople'),
                                href: '/people',
                                permission: 'Permissions.VdkPeople.Read'
                            },
                            {
                                id: 'relations-management',
                                label: t('relations.relationsManagement'),
                                href: '/relations',
                                permission: 'Permissions.ExternalRelation.Read'
                            }
                        ]
                    },
                    {
                        id: 'sponsorships',
                        label: t('common.sponsorships'),
                        href: '/sponsorships',
                        icon: _jsx(Coin, { variant: 'Outline' }),
                        permission: 'Permissions.Partner.Read'
                    }
                ]
            },
            // {
            //   title: (t('common.management') as string).toUpperCase(),
            //   items: [
            //     {
            //       id: 'yearly-plan',
            //       label: 'Yearly Plan',
            //       onClick: () => console.log('Yearly Plan clicked'),
            //       icon: <Calendar2 variant='Outline' />
            //     },
            //     {
            //       id: 'vdk-sky',
            //       label: 'VDK Sky',
            //       onClick: () => console.log('VDK Sky clicked'),
            //       icon: '/assets/images/sky-icon.png'
            //     }
            //   ]
            // },
            {
                title: t('common.management').toUpperCase(),
                items: [
                    {
                        id: 'settings',
                        label: t('common.settings'),
                        href: '/settings',
                        icon: _jsx(Setting2, { variant: 'Outline' })
                    },
                    // {
                    //   id: 'companies',
                    //   label: 'Companies',
                    //   onClick: () => console.log('Companies clicked'),
                    //   icon: <Building variant='Outline' />
                    // },
                    {
                        id: 'users',
                        label: t('common.users'),
                        href: '/users',
                        icon: _jsx(User, { variant: 'Outline' }),
                        permission: 'Permissions.Users.Read'
                    },
                    {
                        id: 'collaborators',
                        label: t('common.collaborators'),
                        href: '/collaborators',
                        icon: _jsx(UserOctagon, { variant: 'Outline' }),
                        permission: 'Permissions.Collaborator.Read'
                    },
                    {
                        id: 'roles-permissions',
                        label: t('common.rolesPermissions'),
                        href: '/roles-permissions',
                        icon: _jsx(Lock, { variant: 'Outline' }),
                        permission: 'Permissions.Roles.Read'
                    }
                ]
            },
            {
                title: t('help.sectionTitle').toUpperCase(),
                items: [
                    {
                        id: 'usage-manual',
                        label: t('help.usageManual.menuLabel'),
                        href: '/usage-manual',
                        icon: _jsx(Book1, { variant: 'Outline' })
                    }
                ]
            }
        ];
        const finalSections = (() => {
            if (!isCollaborator)
                return sections;
            const allowedIds = new Set(['home', 'events', 'tasks', 'usage-manual']);
            const filtered = sections
                .map((section) => ({
                ...section,
                items: section.items.filter((item) => allowedIds.has(item.id))
            }))
                .filter((section) => section.items.length > 0);
            return filtered;
        })();
        setNavigationSections(finalSections);
    }, [i18n.language, t, setNavigationSections, isCollaborator]);
    const handleSearch = useCallback((value) => {
        setSearchValue(value);
        if (onSearch) {
            onSearch(value);
        }
    }, [onSearch]);
    const value = {
        isOpen,
        activeItem,
        navigationSections,
        toggleSidebar,
        setActiveItem: handleSetActiveItem,
        setNavigationSections: handleSetNavigationSections,
        searchValue,
        setSearchValue,
        onSearch: handleSearch
    };
    return _jsx(SidebarContext.Provider, { value: value, children: children });
};
export const useSidebar = () => {
    const context = useContext(SidebarContext);
    if (context === undefined) {
        throw new Error('useSidebar must be used within a SidebarProvider');
    }
    return context;
};
export default SidebarContext;
